import React, { useEffect, useState } from 'react';
import {
  ApiButton,
  Button,
  ErrorInfo,
  Grid,
  TextField,
  TextFieldForm,
  Typography
} from 'web-library';
import { useTranslation } from 'react-i18next';

import { makeStyles } from 'tss-react/mui';
import { useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { useHistory, useParams } from 'react-router-dom';
import AddReasonSchema from './schema/AddReasonSchema';
import { candidateService } from '../../../api';
import { RequestSummaryDetailResponse } from '../../../api-refactored/group-special-consideration/useGetRequestSummary';
import {
  GscDetailFromData,
  PostGscDetailRequest,
  usePostRequestDetail,
} from '../../../api-refactored/group-special-consideration/usePostRequestDetail';
import RouteKey from '../../../routes/key';
import ControlledAutocomplete from '../../../components/form-items/ControlledAutocomplete';
import { TypeAndReasonModel } from '../../../api/candidate/special-consideration/model';
import { useRemainingCharacter } from '../../../hooks/useRemainingCharacter';

const useStyles = makeStyles()((theme) =>
({
  title: {
    marginBottom: theme.spacing(3),
  },
  errMsg: {
    paddingBottom: theme.spacing(2),
  },
  formField: {
    paddingTop: theme.spacing(4),
  },
  actionButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      marginRight: 0,
      marginBottom: theme.spacing(2),
    },
  },
}));

interface ReasonSectionProps {
  disabled: boolean;
  reasonData?: RequestSummaryDetailResponse;
  amendment: boolean;
}

const { specialConsideration: specialConsiderationRoute } = RouteKey.child;
const {
  specialConsideration,
  coordinatorUploadSupportingDocument,
  instructions,
  coordinatorApplicationSummary,
  amendCoordinatorUploadSupportingDocument,
} = RouteKey['special-consideration'].child;

const ReasonSection = ({ disabled, reasonData, amendment }: ReasonSectionProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const history = useHistory();
  const { uuid: applicationUuid } = useParams<{ uuid: string }>();
  const [errorMsg, setErrorMsg] = useState<string | undefined>();
  const [selectedScType, setSelectedScType] = useState<string>(
    reasonData?.specialConsiderationTypeCode ?? ''
  );

  const maximumRemarksLength = 500;
  const { remainingCharMsg, charactersRemainingHandler } = useRemainingCharacter({
    maximumCharLength: maximumRemarksLength,
  });

  const form = useForm<GscDetailFromData>({
    mode: 'onSubmit',
    resolver: joiResolver(AddReasonSchema()),
    defaultValues: {
      explanation: reasonData?.explanation,
      specialConsiderationType: reasonData?.specialConsiderationTypeCode,
      reason: reasonData?.reasonCode,
    },
  });
  const { register, formState: {errors}, handleSubmit, control, reset, setValue } = form;
      useEffect(() => {
    reset({
      explanation: reasonData?.explanation,
      specialConsiderationType: reasonData?.specialConsiderationTypeCode,
      reason: reasonData?.reasonCode,
    });
    charactersRemainingHandler(reasonData?.explanation?.length || 0);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [reasonData]);

  const {
    data: scTypesAndReasonsData,
    isLoading: isLoadingDropdownData,
    error: isDropdownDataError,
    refetch: refetchDropdownData,
  } = candidateService.specialConsideration.candidate.useGetTypesAndReasons();

  const scTypesOption = () => scTypesAndReasonsData?.specialConsiderationTypes ?? [];
  const reasonOption = () =>
    scTypesAndReasonsData?.specialConsiderationTypeReasonMap[selectedScType] ?? [];

  const requestDetailMutation = usePostRequestDetail({
    onSuccess: () => {
      history.push(
        `/${specialConsiderationRoute}/${specialConsideration.route}/${applicationUuid}/${coordinatorUploadSupportingDocument.route}`
      );
    },
    onError: (err) => {
      setErrorMsg(err?.response?.data.message);
    },
  });

  const postGscDetailHandler = (formData: GscDetailFromData) => {
    const data: PostGscDetailRequest = {
      uuid: applicationUuid,
      explanation: formData.explanation,
      specialConsiderationType: formData.specialConsiderationType,
      reason: formData.reason,
    };
    requestDetailMutation.mutate(data);
  };

  return (
    <>
      <Typography variant="h3" className={classes.title}>
        {t('specialConsideration->>Dialog.SelectScTypeAndReason')}
      </Typography>
      <form onSubmit={handleSubmit((formData) => postGscDetailHandler(formData))}>
        <ControlledAutocomplete<TypeAndReasonModel, false, false, undefined>
          placeholder={t(
            'specialConsideration->>ApplyForSpecialConsideration.SelectSpecialConsiderationType'
          )}
          label={t('specialConsideration->>UploadRankingDetail.SpecialConsiderationType')}
          name="specialConsiderationType"
          id="inputSelect-specialConsiderationType"
          options={scTypesOption()}
          control={control}
          error={!!errors.specialConsiderationType}
          helperText={errors?.specialConsiderationType?.message?.toString()}
          getOptionLabel={(option: TypeAndReasonModel) => option.children}
          transform={{
            input: (selectedOption, options) =>
              options.find((option) => option.value === selectedOption) ?? null,
            output: (selectedOption) => {
              const selectValue = selectedOption ? selectedOption.value : '';
              setSelectedScType(selectValue);
              setValue('reason', '');
              return selectValue;
            },
          }}
          isRequired
          loading={isLoadingDropdownData}
          loadingError={!!isDropdownDataError}
          loadingErrorOnReFetch={refetchDropdownData}
          disabled={disabled || amendment}
        />
        <ControlledAutocomplete<TypeAndReasonModel, false, false, undefined>
          className={classes.formField}
          placeholder={t('specialConsideration->>ApplyForSpecialConsideration.SelectReason')}
          label={t('specialConsideration->>UploadRankingDetail.Reason')}
          name="reason"
          id="inputSelect-reason"
          options={reasonOption()}
          control={control}
          error={!!errors.reason}
          helperText={errors?.reason?.message?.toString()}
          getOptionLabel={(option: TypeAndReasonModel) => option.children}
          transform={{
            input: (selectedOption, options) =>
              options.find((option) => option.value === selectedOption) ?? null,
            output: (selectedOption) => (selectedOption ? selectedOption.value : ''),
          }}
          isRequired
          loading={isLoadingDropdownData}
          loadingError={!!isDropdownDataError}
          loadingErrorOnReFetch={refetchDropdownData}
          disabled={disabled || amendment}
        />
        <TextFieldForm
          className={classes.formField}
          form={form}
          label={t('specialConsideration->>ApplyForSpecialConsideration.Remarks')}
          subLabel={t('specialConsideration->>ApplyForSpecialConsideration.RemarksSubLabel')}
          name="explanation"
          placeholder={t('specialConsideration->>ApplyForSpecialConsideration.EnterRemarks')}
          type="text"
          error={!!errors.explanation}
          helperText={errors?.explanation ? errors?.explanation?.message : remainingCharMsg}
          inputProps={{
            maxLength: maximumRemarksLength,
          }}
          onChange={({ target }) => charactersRemainingHandler(target.value.length)}
          fullWidth
          multiline
          rows={4}
          isRequired
          disabled={disabled || amendment}
        />
        <>
          <ErrorInfo className={classes.errMsg} label={errorMsg || ''} />
          <Grid container>
            <Button
              id="button-reasonSection-cancel"
              variant="outlined"
              onClick={() => {
                if (amendment) {
                  history.push(`${coordinatorApplicationSummary.route}`);
                } else {
                  history.push(
                    `/${specialConsiderationRoute}/${specialConsideration.route}/${instructions.route}`,
                    {
                      isContinueApplication: true,
                      applicationUuid,
                    }
                  );
                }
              }}
              className={classes.actionButton}
            >
              {t('specialConsideration->>UploadRankingDetail.Back')}
            </Button>
            {amendment ? (
              <Button
                id="button-reasonSection-amendGroupSC"
                className={classes.actionButton}
                onClick={() => history.push(`${amendCoordinatorUploadSupportingDocument.route}`)}
              >
                {t('specialConsideration->>UploadRankingDetail.ConfirmRankingDetails')}
              </Button>
            ) : (
              <ApiButton
                id="button-reasonSection-postRequestDetail"
                status={requestDetailMutation.status}
                type="submit"
                className={classes.actionButton}
                disabled={disabled}
              >
                {t('specialConsideration->>UploadRankingDetail.ConfirmRankingDetails')}
              </ApiButton>
            )}
          </Grid>
        </>
      </form>
    </>
  );
};
export default ReasonSection;
